import cloneDeep from 'lodash/cloneDeep'

import { digObject, sortArrayBy } from '@domain-group/javascript-utils'

import type { AgencyModel, CustomFieldModel } from '@models/types'

import useCustomFields from './useCustomFields'
import useCustomFieldSets from './useCustomFieldSets'

export type CustomFieldsGroup = {
  id: string,
  items: CustomFieldModel[],
  label: string,
  customFieldSetId: number,
  tag: string,
}

const groupCustomFieldsByTag = (customFields: CustomFieldModel[]) => {
  const groups: { [key: string]: CustomFieldsGroup } = {}

  customFields.forEach((customField) => {
    // Adding a default value 'Custom Fields' in case field is not tagged
    const tags: string[] = digObject(customField, 'options.tags', [])
    if (!tags.length) tags.push('Custom Fields')

    tags.forEach((tag) => {
      // Create Group
      if (!groups[tag]){
        groups[tag] = {
          id: tag,
          items: [],
          label: tag,
          customFieldSetId: customField.owner_id,
          tag,
        }
      }

      // Add Field
      groups[tag].items.push(customField)
    })
  })

  return groups
}

// Removes number from label e.g. '001 - Basic Features' => 'Basic Features'
// if no number, just return the label e.g. 'Basic Features' => 'Basic Features'
export const generateCustomFieldLabels = (groupedCustomFields: { [key: string]: CustomFieldsGroup }) => {
  const array = Object.values(groupedCustomFields).map((customField) => {
    const { label } = customField
    const [sort, strippedLabel] = label.split(' - ')

    return {
      ...cloneDeep(customField),
      label: strippedLabel || label,
      sort,
    }
  })

  return array
}

const sortCustomFieldGroups = (
  customFieldGroups: ReturnType<typeof generateCustomFieldLabels>,
) => sortArrayBy(customFieldGroups, 'asc', 'sort')

type UseEntityTypeCustomFieldsParams = {
  entityType?: string,
  options?: {
    agency: AgencyModel,
    hidden?: boolean,
    performHttpRequests?: boolean,
  },
  deps?: any,
}

const useEntityTypeCustomFields = (params: UseEntityTypeCustomFieldsParams) => {
  const { entityType, options, deps } = params
  const { agency, performHttpRequests } = options || {}

  const missingDepValues = deps ? deps.filter((i: any) => i === undefined || i === null) : []

  const customFieldSetsPayload = useCustomFieldSets({
    filters: {
      entity_type: entityType,
      // @ts-expect-error TS18048
      owner_id: agency.brand_id,
      owner_type: 'Brand',
    },
    performHttpRequests: performHttpRequests && !missingDepValues.length && entityType,
  })

  const { filteredCustomFieldSets } = customFieldSetsPayload
  const customFieldSet = filteredCustomFieldSets[0]

  // Filter Fields
  const customFieldsPayload = useCustomFields({
    filters: {
      owner_type: 'CustomFieldSet',
      owner_id: customFieldSet?.id,
    },
  })
  const { filteredCustomFields } = customFieldsPayload

  // Grouped Fields
  const groupedCustomFields = groupCustomFieldsByTag(filteredCustomFields)

  const labelledCustomFields = generateCustomFieldLabels(groupedCustomFields)

  const sortedCustomFieldGroups = sortCustomFieldGroups(labelledCustomFields)

  // Required Fields
  const requiredFields = filteredCustomFields.filter(
    field => field.required,
  ).map(field => ({ key: field.key }))

  return {
    customFieldSet,
    filteredCustomFields,
    groupedCustomFields,
    requiredFields,
    sortedCustomFieldGroups,
  }
}

export default useEntityTypeCustomFields
